import { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { clientsService } from '../../services/api';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  address_street: '',
  address_suburb: '',
  address_state: 'QLD',
  address_postcode: '',
  notes: ''
};

export default function ClientModal({ isOpen, onClose, onSave, client = null }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!client;

  useEffect(() => {
    if (isOpen) {
      setForm(client ? { ...INITIAL_FORM, ...client, address_state: client.address_state || 'QLD' } : INITIAL_FORM);
      setErrors({});
    }
  }, [isOpen, client]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email format';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const response = isEdit
        ? await clientsService.update(client.id, form)
        : await clientsService.create(form);
      if (response.success) {
        onSave(response.customer);
        onClose();
      } else {
        setErrors({ submit: response.error || 'Failed to save client' });
      }
    } catch (err) {
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Client' : 'New Client'} size="medium">
      <form onSubmit={handleSubmit} className="client-form">
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Client name" autoFocus />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="0400 000 000" />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="address_street">Street Address</label>
          <input id="address_street" name="address_street" value={form.address_street} onChange={handleChange} placeholder="123 Example Street" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address_suburb">Suburb</label>
            <input id="address_suburb" name="address_suburb" value={form.address_suburb} onChange={handleChange} placeholder="Hope Island" />
          </div>
          <div className="form-group">
            <label htmlFor="address_state">State</label>
            <select id="address_state" name="address_state" value={form.address_state} onChange={handleChange}>
              {['QLD', 'NSW', 'VIC', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="address_postcode">Postcode</label>
            <input id="address_postcode" name="address_postcode" value={form.address_postcode} onChange={handleChange} placeholder="4212" maxLength="4" />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea id="notes" name="notes" value={form.notes} onChange={handleChange} rows="3" placeholder="Any additional notes about this client..." />
        </div>

        {errors.submit && <div className="form-error text-center mb-md">{errors.submit}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
